import { CompanyInfo } from '@shared/models/companyInfo';
import { Injectable, inject } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Observable, throwError, switchMap, of, firstValueFrom } from 'rxjs';

import { catchError } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { CreateUserRequest } from '@features/booking/calender/model/models';
import { User } from '@shared/models/user';
import { UserCheck } from '@shared/models/userCheck';
import { PagedBookingList } from '@features/booking/calender/model/pagedBookingList';

import { AuthenticationService } from '@core/authentication/authentication.service';
import { Association } from '@app/features/associations/models/association';

const APIURL = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private http = inject(HttpClient);
  private authenticationService = inject(AuthenticationService);

  private associationsPromise: Promise<Association[] | null> | null = null;

  getUsers(name?: string, start?: string, end?: string) {
    let paramUser = '?';

    if (name) {
      paramUser += `&search=${name}`;
    }

    if (start && end) {
      paramUser += `&start=${start}&end=${end}`;
    }

    return this.http.get<User[]>(`${APIURL}/users/${paramUser}`);
  }

  createUser(payload: CreateUserRequest): Observable<User> {
    return this.http.post<User>(APIURL + '/users/?notify_email=true', payload);
  }

  getUser(id: number) {
    return this.http.get<User>(APIURL + `/users/${id}?expand=locations`).pipe(
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }

  updateUser(user: User): Observable<User> {
    return this.http.put<User>(APIURL + `/users/${user.id}`, user);
  }

  // deleteUser(user: User): Observable<User> {
  //   return this.http.delete<User>(APIURL + `/users/${user.id}`);
  // }

  getUserBookings(id: number, page: number, pageSize: number) {
    return this.http.get<PagedBookingList>(
      `${APIURL}/users/${id}/bookings/?page=${page}&pageSize=${pageSize}&expand=bookingperiods`,
    );
  }

  checkUsername(username: string): Observable<UserCheck> {
    return this.http.get<UserCheck>(APIURL + `/users/username_check/?username=${username}`).pipe(
      catchError(() => {
        return throwError('username check went wrong!');
      }),
    );
  }

  checkEmail(email: string): Observable<UserCheck> {
    return this.http.get<UserCheck>(APIURL + `/users/email_check/?email=${email}`).pipe(
      catchError(() => {
        return throwError('email check  went wrong!');
      }),
    );
  }

  checkMobile(mobile: number): Observable<UserCheck> {
    return this.http.get<UserCheck>(APIURL + `/users/mobile_check/?mobile=${mobile}`).pipe(
      catchError(() => {
        return throwError('mobile check went wrong!');
      }),
    );
  }

  getCompanyInfo(id: number): Observable<CompanyInfo> {
    return this.http.get(APIURL + `/users/${id}/companyinfo`);
  }

  updateCompanyInfo(id: number, payload: CompanyInfo): Observable<CompanyInfo> {
    return this.http.put(APIURL + `/users/${id}/companyinfo`, payload);
  }

  getAssociations(): Promise<Association[] | null> {
    if (this.associationsPromise) {
      return this.associationsPromise; // Return the existing promise if a fetch is already in progress
    }

    const associations$ = this.authenticationService.getLoggedUser().pipe(
      switchMap((user) => {
        return user
          ? this.http.get<Association[]>(`${APIURL}/users/${user.id}/associations`)
          : of(null); // Return null if no user is logged in
      }),
    );

    this.associationsPromise = firstValueFrom(associations$); // Cache the promise for reuse

    return this.associationsPromise.finally(() => {
      this.associationsPromise = null; // Reset the promise after it resolves
    });
  }

  subscriptionCancel = (id: number, userSubscriptionId: number) => {
    const payload = {
      userSubscriptionId: userSubscriptionId,
    };
    return this.http.post(`${APIURL}/users/${id}/subscriptions/cancel`, payload);
  };
}
